import { useLocation, useNavigate } from 'react-router-dom';
import logoWhite from '../../assets/obriens-logo-white.png';
import { useUserRole } from '@/hooks/useUserRole';

interface SidebarProps {
  /** Clicking the logo opens the hidden About panel (SPEC §7 easter egg). */
  onLogoClick: () => void;
}

// Home and Customers are wired; the rest arrive in later milestones (disabled).
// `admin` items only render for admins.
const NAV: { label: string; to?: string; admin?: boolean }[] = [
  { label: 'Home', to: '/' },
  { label: 'Actions' },
  { label: 'Customers', to: '/customers' },
  { label: 'Promotions', to: '/promotions' },
  { label: 'Reports' },
  { label: 'Admin', to: '/admin', admin: true },
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

export function Sidebar({ onLogoClick }: SidebarProps) {
  const { user, isAdmin, seesAll, stores } = useUserRole();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isActive = (to?: string) =>
    to === '/' ? pathname === '/' : !!to && pathname.startsWith(to);

  const displayName = user?.displayName ?? 'Signing in…';
  const storeLabel = seesAll
    ? 'All stores'
    : stores.map((s) => s.code).join(', ') || '—';

  const nav = NAV.filter((item) => !item.admin || isAdmin);

  return (
    <aside className="sidebar">
      <button type="button" className="sidebar-logo" onClick={onLogoClick} aria-label="About">
        <img src={logoWhite} alt="O'Briens" />
      </button>

      <nav className="sidebar-nav" aria-label="Main">
        {nav.map((item) =>
          item.to ? (
            <button
              key={item.label}
              type="button"
              className={`sidebar-nav-item${isActive(item.to) ? ' is-active' : ''}`}
              onClick={() => navigate(item.to!)}
            >
              {item.label}
            </button>
          ) : (
            <span key={item.label} className="sidebar-nav-item is-disabled" aria-disabled="true">
              {item.label}
            </span>
          )
        )}
      </nav>

      {/* User block at the bottom (SPEC §7): avatar initials + name + store. */}
      <div className="sidebar-user">
        <div className="sidebar-user-row">
          <span className="sidebar-avatar">{initials(displayName)}</span>
          <span className="sidebar-user-meta">
            <span className="sidebar-user-name">{displayName}</span>
            <span className="sidebar-user-store">{storeLabel}</span>
          </span>
        </div>
      </div>
    </aside>
  );
}
